import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Globe,
  User,
  Check,
  Key,
  Eye,
  EyeOff,
  Trash2,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { t, language, changeLanguage } = useLanguage();
  const { user } = useAuth();
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState({
    openai_api_key: '',
    google_api_key: '',
    use_own_keys: false
  });
  const [showOpenAI, setShowOpenAI] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [savingKeys, setSavingKeys] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/users/api-keys`, { withCredentials: true });
      setApiKeys({
        openai_api_key: response.data.openai_api_key || '',
        google_api_key: response.data.google_api_key || '',
        use_own_keys: response.data.use_own_keys || false
      });
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleSaveApiKeys = async () => {
    setSavingKeys(true);
    try {
      await axios.put(`${API}/users/api-keys`, apiKeys, { withCredentials: true });
      toast.success(language === 'es' ? 'API Keys guardadas correctamente' : 'API Keys saved successfully');
      fetchApiKeys(); // Refresh to get masked keys
    } catch (error) {
      console.error('Error saving API keys:', error);
      toast.error(language === 'es' ? 'Error al guardar API Keys' : 'Error saving API Keys');
    } finally {
      setSavingKeys(false);
    }
  };

  const handleDeleteApiKeys = async () => {
    if (!window.confirm(language === 'es' ? '¿Eliminar todas las API Keys?' : 'Delete all API Keys?')) return;
    
    try {
      await axios.delete(`${API}/users/api-keys`, { withCredentials: true });
      setApiKeys({ openai_api_key: '', google_api_key: '', use_own_keys: false });
      toast.success(language === 'es' ? 'API Keys eliminadas' : 'API Keys deleted');
    } catch (error) {
      console.error('Error deleting API keys:', error);
      toast.error(language === 'es' ? 'Error al eliminar API Keys' : 'Error deleting API Keys');
    }
  };

  const handleLanguageChange = async (newLang) => {
    changeLanguage(newLang);
    try {
      await axios.put(`${API}/users/language`, { language: newLang }, { withCredentials: true });
      toast.success(language === 'es' ? 'Idioma actualizado' : 'Language updated');
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {t('settings')}
          </h1>
          <p className="text-zinc-400 mt-1">
            {language === 'es' ? 'Personaliza tu experiencia en Brain-GL' : 'Customize your Brain-GL experience'}
          </p>
        </div>

        {/* Profile Card */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-lime-500" />
              {t('profile')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-lime-500 text-black text-xl">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-lg font-semibold text-white">{user?.name}</p>
                <p className="text-sm text-zinc-400">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* API Keys Settings */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-lime-500" />
              {language === 'es' ? 'API Keys Propias' : 'Custom API Keys'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {loadingKeys ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-lime-500" />
              </div>
            ) : (
              <>
                {/* Switch to use own keys */}
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="space-y-1">
                    <p className="font-medium text-white">
                      {language === 'es' ? 'Usar mis propias API Keys' : 'Use my own API Keys'}
                    </p>
                    <p className="text-sm text-zinc-400">
                      {language === 'es' 
                        ? 'Conecta tus cuentas de OpenAI y Google para usar tus propios límites' 
                        : 'Connect your OpenAI and Google accounts to use your own limits'}
                    </p>
                  </div>
                  <Switch
                    checked={apiKeys.use_own_keys}
                    onCheckedChange={(checked) => setApiKeys({...apiKeys, use_own_keys: checked})}
                  />
                </div>

                {/* OpenAI API Key */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">
                    OpenAI API Key
                  </Label>
                  <div className="relative">
                    <Input
                      type={showOpenAI ? "text" : "password"}
                      value={apiKeys.openai_api_key}
                      onChange={(e) => setApiKeys({...apiKeys, openai_api_key: e.target.value})}
                      placeholder="sk-..."
                      className="bg-zinc-800/50 border-zinc-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOpenAI(!showOpenAI)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    >
                      {showOpenAI ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {language === 'es' 
                      ? 'Obtén tu API Key en platform.openai.com' 
                      : 'Get your API Key at platform.openai.com'}
                  </p>
                </div>

                {/* Google API Key */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">
                    Google AI API Key (Gemini)
                  </Label>
                  <div className="relative">
                    <Input
                      type={showGoogle ? "text" : "password"}
                      value={apiKeys.google_api_key}
                      onChange={(e) => setApiKeys({...apiKeys, google_api_key: e.target.value})}
                      placeholder="AIza..."
                      className="bg-zinc-800/50 border-zinc-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGoogle(!showGoogle)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    >
                      {showGoogle ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {language === 'es' 
                      ? 'Obtén tu API Key en aistudio.google.com' 
                      : 'Get your API Key at aistudio.google.com'}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleSaveApiKeys}
                    disabled={savingKeys}
                    className="bg-lime-500 hover:bg-lime-600 text-black"
                  >
                    {savingKeys ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {language === 'es' ? 'Guardar' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteApiKeys}
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Eliminar Keys' : 'Delete Keys'}
                  </Button>
                </div>

                {/* Info box */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400">
                    {language === 'es' 
                      ? '💡 Si activas "Usar mis propias API Keys", el sistema usará tus keys cuando estén disponibles. Si no tienes key para un proveedor específico, se usará el Emergent LLM Key automáticamente.' 
                      : '💡 If you enable "Use my own API Keys", the system will use your keys when available. If you don\'t have a key for a specific provider, Emergent LLM Key will be used automatically.'}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Language Settings */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-lime-500" />
              {t('language')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Label className="text-xs font-medium uppercase text-zinc-500">
              {language === 'es' ? 'Selecciona tu idioma preferido' : 'Select your preferred language'}
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => handleLanguageChange('es')}
                className={`justify-start gap-3 h-14 ${
                  language === 'es' 
                    ? 'bg-lime-500/10 border-lime-500/50 text-lime-500' 
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                }`}
                data-testid="lang-es"
              >
                <span className="text-2xl">🇪🇸</span>
                <div className="text-left">
                  <p className="font-medium">{t('spanish')}</p>
                  <p className="text-xs opacity-70">Español</p>
                </div>
                {language === 'es' && <Check className="w-4 h-4 ml-auto" />}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleLanguageChange('en')}
                className={`justify-start gap-3 h-14 ${
                  language === 'en' 
                    ? 'bg-lime-500/10 border-lime-500/50 text-lime-500' 
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                }`}
                data-testid="lang-en"
              >
                <span className="text-2xl">🇺🇸</span>
                <div className="text-left">
                  <p className="font-medium">{t('english')}</p>
                  <p className="text-xs opacity-70">English</p>
                </div>
                {language === 'en' && <Check className="w-4 h-4 ml-auto" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Brain-GL</p>
                <p className="text-xs text-zinc-500">v1.0.0 • by Grupo Locoff</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500">
                  {language === 'es' ? 'Inteligencia y Marketing' : 'Intelligence and Marketing'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
